import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import StatisticsModal from '../components/StatisticsModal';
import './records.css';

// 디바운싱 훅 - 객체를 위한 개선된 버전
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // 객체의 경우 JSON.stringify로 비교
    const currentValueStr = JSON.stringify(value);
    const debouncedValueStr = JSON.stringify(debouncedValue);
    
    // 값이 동일하면 타이머 설정하지 않음
    if (currentValueStr === debouncedValueStr) {
      return;
    }

    // 빈 값들인 경우 즉시 반영
    if (typeof value === 'object') {
      const hasValue = Object.values(value).some(v => v && v.toString().trim() !== '');
      if (!hasValue) {
        setDebouncedValue(value);
        return;
      }
    } else if (value === '' || value == null) {
      setDebouncedValue(value);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay, debouncedValue]);

  return debouncedValue;
};

// 다중선택 드롭다운 컴포넌트
const MultiSelectDropdown = ({ options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const displayText = selected.length === 0 ? null : selected.map(val => {
    const opt = options.find(o => (typeof o === 'object' ? o.value : o) === val);
    return opt ? (typeof opt === 'object' ? opt.label : opt) : val;
  }).join(', ');

  return (
    <div className="multi-select" ref={containerRef}>
      <button
        type="button"
        className={`multi-select-trigger${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className={`multi-select-text${!displayText ? ' placeholder' : ''}`}>
          {displayText || placeholder}
        </span>
        <span className="multi-select-arrow">▼</span>
      </button>
      {isOpen && (
        <div className="multi-select-panel">
          {options.map(opt => {
            const value = typeof opt === 'object' ? opt.value : opt;
            const label = typeof opt === 'object' ? opt.label : opt;
            const checked = selected.includes(value);
            return (
              <label key={value} className={`multi-select-item${checked ? ' checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onChange(value)}
                />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// 트로피 아이콘 컴포넌트 (서버에서 isTrophy, isRare 필드 사용)
const TrophyIcon = React.memo(({ record }) => {
  const { isTrophy, isRare } = record;

  const icons = [];

  // isRare가 true이면 rare trophy 아이콘 표시
  if (isRare) {
    icons.push(
      <img
        key="rare-trophy"
        src="/icon/flag-trophy-rare.webp"
        alt="레어 트로피"
        className="trophy-icon"
        title="레어 트로피"
      />
    );
  }
  // isTrophy가 true이면 일반 trophy 아이콘 표시
  else if (isTrophy) {
    icons.push(
      <img
        key="trophy"
        src="/icon/flag-trophy.webp"
        alt="트로피"
        className="trophy-icon"
        title="트로피"
      />
    );
  }
  return <>{icons}</>;
});

// 기록 테이블 행 컴포넌트 (메모이제이션)
const RecordRow = React.memo(({ 
  record, 
  formatDate, 
  translateCategory, 
  translateRegion,
  getFlagImage,
  formatWeight,
  processBait,
  handleLocationClick,
  handleFishClick,
  handleBaitClick,
  baitSystemIds
}) => {
  return (
    <tr>
      <td>{formatDate(record.date, record.created_at)}</td>
      <td>
        <div 
          className="location-name clickable" 
          onClick={() => handleLocationClick(record.location)}
          title={`"${record.location}" 지역 통계 보기`}
        >
          {record.location}
        </div>
      </td>
      <td>{translateCategory(record.category)}</td>
      <td>
        <div 
          className="fish-name clickable" 
          onClick={() => handleFishClick(record.fish)}
          title={`"${record.fish}" 어종 통계 보기`}
        >
          <TrophyIcon record={record} />
          {record.fish}
        </div>
      </td>
      <td className="weight-cell">{formatWeight(record.weight)}</td>
      <td className="bait-container">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
            {processBait(record).map((bait, baitIdx) => {
              const imageUrl = baitSystemIds.get(bait);
              return (
                <div 
                  key={baitIdx}
                  className="bait-item clickable" 
                  onClick={() => handleBaitClick(bait)}
                  title={`"${bait}" 미끼 통계 보기`}
                >
                  {imageUrl && (
                    <img
                      src={`${imageUrl}.webp`}
                      alt={bait}
                      className="bait-image"
                      onError={(e) => {
                        console.warn('Failed to load bait image:', bait);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{bait}</span>
                </div>
              );
            })}
          </div>
          <div className="player-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 'bold' }}>
            <img 
              src={getFlagImage(record.region)} 
              alt={`${translateRegion(record.region)} 국기`}
              title={translateRegion(record.region)}
              className="flag-image"
              onError={(e) => {
                e.target.src = '/icon/flags/world.webp';
              }}
            />
            <span>{record.player}</span>
          </div>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // record와 baitSystemIds가 동일하면 재렌더링하지 않음
  return prevProps.record.id === nextProps.record.id && 
         prevProps.baitSystemIds === nextProps.baitSystemIds;
});

const Records = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // API 요청 중복 방지를 위한 ref
  const lastRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // 미끼 시스템 ID 매핑
  const [baitSystemIds, setBaitSystemIds] = useState(new Map());

  // 채비 번역 매핑 (컴포넌트 외부로 이동하거나 useMemo로 감싸기)
  const categoryTranslations = useMemo(() => ({
    'records': '통합기록',
    'ultralight': 'UL',
    'recordslight': '라이트',
    'bottomlight': '바텀 라이트',
    'telestick': '텔레스코픽'
  }), []);

  // 지역 번역 매핑 (컴포넌트 외부로 이동하거나 useMemo로 감싸기)
  const regionTranslations = useMemo(() => ({
    'gl': '모든 지역',
    'ru': '러시아',
    'de': '독일',
    'us': '미국',
    'fr': '프랑스',
    'cn': '중국',
    'pl': '폴란드',
    'kr': '한국',
    'jp': '일본',
    'en': '기타 국가',
    'id': '인도네시아'
  }), []);
  
  // 검색 필터 상태 (즉시 반영되는 필터들)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    regions: [],
    categories: [],
    locations: [],
    trophyFilter: [],
    sortBy: 'time',
  });

  // 디바운싱이 필요한 입력 필터들
  const [inputFilters, setInputFilters] = useState({
    player: '',
    bait: '',
    minWeight: '',
    fish: '', // fish 필터를 디바운싱 대상에 추가
  });

  // 모든 입력 필터를 하나로 디바운싱
  const debouncedInputFilters = useDebounce(inputFilters, 500);

  // 필터 옵션들 상태
  const [filterOptions, setFilterOptions] = useState({
    regions: [],
    categories: [],
    locations: []
  });

  // 모달 상태
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: '', // 'bait' or 'fish' or 'location'
    itemName: ''
  });

  // 전체 수역 통계 모달 상태
  const [allLocationsModalOpen, setAllLocationsModalOpen] = useState(false);
  const [locationStats, setLocationStats] = useState([]);
  const [locationStatsLoading, setLocationStatsLoading] = useState(false);
  
  // 전체 수역 통계 모달 날짜 필터
  const [modalDateFilters, setModalDateFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [modalActiveDateRange, setModalActiveDateRange] = useState('');

  // 날짜 빠른 설정 상태
  const [activeDateRange, setActiveDateRange] = useState('');

  // 마지막 갱신 시간 및 전체 개수 가져오기
  const [lastUpdated, setLastUpdated] = useState(null);

  // 전체 수역 통계 가져오기 (날짜 필터 적용)
  const fetchAllLocationStats = useCallback(async (customDateFilters = null) => {
    // 날짜 포맷팅 함수
    const formatDate = (date) => {
      const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
      const year = koreanTime.getUTCFullYear();
      const month = String(koreanTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(koreanTime.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // 오늘 날짜 계산
    const today = new Date();
    const todayFormatted = formatDate(today);
    
    // 사용할 날짜 필터 결정
    const dateFilters = customDateFilters || {
      startDate: todayFormatted,
      endDate: todayFormatted
    };
    
    // 모달 먼저 열기
    setAllLocationsModalOpen(true);
    setLocationStatsLoading(true);
    setLocationStats([]);
    
    // 모달 날짜 필터 초기화 (처음 열 때만)
    if (!customDateFilters) {
      setModalDateFilters({
        startDate: todayFormatted,
        endDate: todayFormatted
      });
      setModalActiveDateRange('today');
    }
    
    try {
      const queryParams = new URLSearchParams({
        startDate: dateFilters.startDate,
        endDate: dateFilters.endDate
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'locations' || key === 'startDate' || key === 'endDate') return;
        if (Array.isArray(value)) {
          if (value.length > 0) queryParams.set(key, value.join(','));
        } else if (value !== '' && value !== null && value !== undefined) {
          queryParams.set(key, value);
        }
      });
      
      const res = await fetch(`/api/locations/statistics?${queryParams}`);
      if (res.ok) {
        const data = await res.json();
        setLocationStats(data);
      } else {
        console.error('수역 통계 로딩 실패');
        setLocationStats([]);
      }
    } catch (error) {
      console.error('수역 통계 로딩 에러:', error);
      setLocationStats([]);
    } finally {
      setLocationStatsLoading(false);
    }
  }, [filters]);

  // 모달 날짜 필터 변경 핸들러
  const handleModalDateChange = (key, value) => {
    const newFilters = {
      ...modalDateFilters,
      [key]: value
    };
    setModalDateFilters(newFilters);
    setModalActiveDateRange(''); // 수동 변경 시 퀵셀렉트 해제
    
    // 날짜가 변경되면 즉시 데이터 재조회
    fetchAllLocationStats(newFilters);
  };

  // 모달 날짜 빠른 설정 함수
  const setModalDateRange = (range) => {
    const today = new Date();
    const formatDate = (date) => {
      const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
      const year = koreanTime.getUTCFullYear();
      const month = String(koreanTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(koreanTime.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    let startDate = '';
    let endDate = formatDate(today);
    
    switch (range) {
      case 'today':
        startDate = formatDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(today);
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = formatDate(weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        startDate = formatDate(monthAgo);
        break;
      default:
        return;
    }
    
    const newFilters = { startDate, endDate };
    setModalDateFilters(newFilters);
    setModalActiveDateRange(range);
    
    // 데이터 재조회
    fetchAllLocationStats(newFilters);
  };




  // 지역별 국기 이미지 매핑
  const getFlagImage = useCallback((region) => {
    const flagMap = {
      'gl': '/icon/flags/world.webp', // 모든 지역
      'en': '/icon/flags/world.webp', // 기타 지역도 글로벌 이미지 사용
      'ru': '/icon/flags/ru.webp',
      'de': '/icon/flags/de.webp',
      'us': '/icon/flags/us.webp',
      'fr': '/icon/flags/fr.webp',
      'cn': '/icon/flags/cn.webp',
      'pl': '/icon/flags/pl.webp',
      'kr': '/icon/flags/kr.webp',
      'jp': '/icon/flags/jp.webp',
      'id': '/icon/flags/id.webp'
    };
    
    return flagMap[region] || '/icon/flags/world.webp'; // 기본값
  }, []);

  // 미끼 처리 함수 (bait1, bait2 컬럼에서 배열로 처리)
  const processBait = useCallback((record) => {
    const baits = [];
    if (record.bait1) baits.push(record.bait1.trim());
    if (record.bait2) baits.push(record.bait2.trim());
    
    return baits.length > 0 ? baits : [''];
  }, []);

  // 미끼 systemId 배치 조회 함수
  const fetchBaitSystemIds = useCallback(async (baitNames) => {
    const uniqueBaitNames = [...new Set(baitNames)];
    const uncachedBaits = uniqueBaitNames.filter(name => !baitSystemIds.has(name));
    
    if (uncachedBaits.length === 0) {
      return;
    }

    try {
      const promises = uncachedBaits.map(async (baitName) => {
        const response = await fetch(`/api/bait-systemid/${encodeURIComponent(baitName)}`);
        const data = await response.json();
        return { baitName, imageUrl: data.imageUrl };
      });

      const results = await Promise.all(promises);
      
      setBaitSystemIds(prev => {
        const newMap = new Map(prev);
        results.forEach(({ baitName, imageUrl }) => {
          newMap.set(baitName, imageUrl);
        });
        return newMap;
      });
    } catch (error) {
      console.error('미끼 systemId 배치 조회 실패:', error);
    }
  }, [baitSystemIds]);

  // 모달 관련 핸들러 (BaitWithImage보다 먼저 정의)
  const handleBaitClick = useCallback((baitName) => {
    setModalState({
      isOpen: true,
      type: 'bait',
      itemName: baitName
    });
  }, []);

  const handleFishClick = useCallback((fishName) => {
    setModalState({
      isOpen: true,
      type: 'fish',
      itemName: fishName
    });
  }, []);

  const handleLocationClick = useCallback((locationName) => {
    setModalState({
      isOpen: true,
      type: 'location',
      itemName: locationName
    });
  }, []);

  // 채비 번역 함수
  const translateCategory = useCallback((category) => {
    return categoryTranslations[category] || category;
  }, [categoryTranslations]);

  // 지역 번역 함수
  const translateRegion = useCallback((region) => {
    return regionTranslations[region] || region;
  }, [regionTranslations]);

  // 필터 옵션 가져오기
  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await fetch(`/api/records/filter-options?type=weekly`);
      if (!res.ok) throw new Error('필터 옵션을 불러올 수 없습니다');
      
      const options = await res.json();
      setFilterOptions(options);
    } catch (err) {
      console.error('필터 옵션 로딩 에러:', err);
    }
  }, []);

  // 마지막 갱신 시간 및 전체 데이터 개수 가져오기
  const fetchLastUpdated = useCallback(async () => {
    try {
      const res = await fetch('/api/records/last-updated');
      if (res.ok) {
        const data = await res.json();
        setLastUpdated(data.lastUpdated);
        setTotalCount(data.totalCount);
      }
    } catch (err) {
      console.error('마지막 갱신 시간 및 전체 개수 로딩 에러:', err);
    }
  }, []);

  // 데이터 가져오기
  const fetchData = useCallback(async () => {
    // 이전 요청이 진행 중이면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const queryParams = new URLSearchParams({ type: 'weekly', limit: '100' });
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) queryParams.set(key, value.join(','));
      } else if (value !== '' && value !== null) {
        queryParams.set(key, value);
      }
    });
    if (debouncedInputFilters.player) queryParams.set('player', debouncedInputFilters.player);
    if (debouncedInputFilters.bait) queryParams.set('bait', debouncedInputFilters.bait);
    if (debouncedInputFilters.minWeight) queryParams.set('minWeight', debouncedInputFilters.minWeight);
    if (debouncedInputFilters.fish) queryParams.set('fish', debouncedInputFilters.fish);

    const requestKey = queryParams.toString();
    
    // 동일한 요청이면 중복 실행 방지
    if (lastRequestRef.current === requestKey) {
      return;
    }
    
    lastRequestRef.current = requestKey;
    setLoading(true);
    setError('');
    
    try {
      // 데이터 50개
      const res = await fetch(`/api/records?${queryParams}`, { signal });
      if (!res.ok) throw new Error('데이터를 불러올 수 없습니다');
      const result = await res.json();
      
      // 요청이 취소되지 않았을 때만 상태 업데이트
      if (!signal.aborted) {
        setData(result);
        setLoading(false);
      }
    } catch (err) {
      // AbortError는 무시
      if (err.name === 'AbortError') {
        return;
      }
      
      console.error('데이터 로딩 에러:', err);
      setError(err.message);
      setData([]);
      setLoading(false);
    }
  }, [filters, debouncedInputFilters]);

  // 미끼 이미지 로딩을 위한 별도 useEffect
  useEffect(() => {
    if (data.length > 0) {
      const allBaitNames = [];
      data.forEach(record => {
        // bait1, bait2 컬럼에서 미끼 수집
        if (record.bait1) {
          allBaitNames.push(record.bait1.trim());
        }
        if (record.bait2) {
          allBaitNames.push(record.bait2.trim());
        }
      });
      if (allBaitNames.length > 0) {
        fetchBaitSystemIds(allBaitNames);
      }
    }
  }, [data, fetchBaitSystemIds]);

  // 컴포넌트 마운트 시 기본 필터를 일주일로 설정
  useEffect(() => {
    setDateRange('week');
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  useEffect(() => {
    fetchFilterOptions();
    fetchLastUpdated(); // 마지막 갱신 시간과 전체 개수를 먼저 로드
  }, [fetchFilterOptions, fetchLastUpdated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 필터 변경 핸들러
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));

    // 날짜 필터가 수동으로 변경되면 활성 상태 초기화
    if (key === 'startDate' || key === 'endDate') {
      setActiveDateRange('');
    }
  };

  // 다중선택 필터 토글 핸들러
  const handleMultiToggle = (key, value) => {
    setFilters(prev => {
      const arr = prev[key];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      };
    });
  };

  // 필터 초기화
  const resetFilters = () => {
    // 일주일 전부터 오늘까지로 초기화
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const formatDate = (date) => {
      const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
      const year = koreanTime.getUTCFullYear();
      const month = String(koreanTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(koreanTime.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setFilters({
      startDate: formatDate(weekAgo),
      endDate: formatDate(today),
      regions: [],
      categories: [],
      locations: [],
      trophyFilter: [],
      sortBy: 'time',
    });
    setInputFilters({
      player: '',
      bait: '',
      minWeight: '',
      fish: '', // fish 필터도 초기화
    });
    setActiveDateRange('week'); // 날짜 범위 활성 상태를 'week'로 설정
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      type: '',
      itemName: ''
    });
  };

  // 날짜 빠른 설정 함수
  const setDateRange = (range) => {
    const today = new Date();
    // 한국 시간대 기준으로 날짜 포맷팅
    const formatDate = (date) => {
      // 한국 시간대(UTC+9)로 변환
      const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
      const year = koreanTime.getUTCFullYear();
      const month = String(koreanTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(koreanTime.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    let startDate = '';
    let endDate = formatDate(today);
    
    switch (range) {
      case 'today':
        startDate = formatDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(today);
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = formatDate(weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        startDate = formatDate(monthAgo);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate
    }));
    setActiveDateRange(range);
  };

  // 날짜 포맷팅 (created_at이 이미 한국시간으로 저장되어 있다고 가정)
  const formatDate = useCallback((dateString, createdAtString) => {
    // created_at을 직접 사용 (이미 한국시간으로 저장됨)
    const createdAt = new Date(createdAtString);
    
    const year = String(createdAt.getFullYear()); // YYYY
    const month = String(createdAt.getMonth() + 1).padStart(2, '0'); // MM
    const day = String(createdAt.getDate()).padStart(2, '0'); // DD
    const hours = String(createdAt.getHours()).padStart(2, '0'); // HH
    const minutes = String(createdAt.getMinutes()).padStart(2, '0'); // MM
    
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  }, []);

  // 마지막 갱신 시간 포맷팅
  const formatLastUpdated = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // 무게 포맷팅 함수 (1kg 미만은 g으로 표시)
  const formatWeight = useCallback((weight) => {
    const numWeight = parseFloat(weight);
    if (numWeight < 1) {
      return `${Math.round(numWeight * 1000)} g`;
    }
    return `${numWeight.toFixed(3)} kg`;
  }, []);

  return (
    <div className="records-container">
      <h1 className="page-title">기록실</h1>

      {/* 검색 필터 */}
      <div className="search-filters">
        {/* 첫 번째 줄: 시작날짜와 종료날짜 */}
        <div className="filter-row">
          {/* 시작 날짜 */}
          <div className="filter-group">
            <label className="filter-label">시작 날짜:</label>
            <input
              type="date"
              className="filter-input"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          {/* 종료 날짜 */}
          <div className="filter-group">
            <label className="filter-label">종료 날짜:</label>
            <input
              type="date"
              className="filter-input"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>

        {/* 두 번째 줄: 빠른 날짜 설정 */}
        <div className="date-quick-buttons">
          <button
            type="button"
            className={`date-quick-btn ${activeDateRange === 'today' ? 'active' : ''}`}
            onClick={() => setDateRange('today')}
          >
            📅 오늘
          </button>
          <button
            type="button"
            className={`date-quick-btn ${activeDateRange === 'yesterday' ? 'active' : ''}`}
            onClick={() => setDateRange('yesterday')}
          >
            📅 어제
          </button>
          <button
            type="button"
            className={`date-quick-btn ${activeDateRange === 'week' ? 'active' : ''}`}
            onClick={() => setDateRange('week')}
          >
            📅 일주일
          </button>
          <button
            type="button"
            className={`date-quick-btn ${activeDateRange === 'month' ? 'active' : ''}`}
            onClick={() => setDateRange('month')}
          >
            📅 30일
          </button>
        </div>

        {/* 세 번째 줄: 지역, 맵, 채비, 어종 */}
        <div className="filter-row">
          {/* 지역 */}
          <div className="filter-group">
            <label className="filter-label">지역:</label>
            <MultiSelectDropdown
              options={filterOptions.regions.map(r => ({ value: r, label: translateRegion(r) }))}
              selected={filters.regions}
              onChange={(val) => handleMultiToggle('regions', val)}
              placeholder="모든지역"
            />
          </div>

          {/* 맵 */}
          <div className="filter-group">
            <label className="filter-label">맵:</label>
            <MultiSelectDropdown
              options={filterOptions.locations}
              selected={filters.locations}
              onChange={(val) => handleMultiToggle('locations', val)}
              placeholder="전체"
            />
          </div>

          {/* 채비 */}
          <div className="filter-group">
            <label className="filter-label">채비:</label>
            <MultiSelectDropdown
              options={filterOptions.categories.map(c => ({ value: c, label: translateCategory(c) }))}
              selected={filters.categories}
              onChange={(val) => handleMultiToggle('categories', val)}
              placeholder="전체"
            />
          </div>

          {/* 어종 */}
          <div className="filter-group">
            <label className="filter-label">어종:</label>
            <input
              type="text"
              className="filter-input"
              placeholder="어종 입력..."
              value={inputFilters.fish}
              onChange={(e) => setInputFilters(prev => ({ ...prev, fish: e.target.value }))}
            />
          </div>
        </div>

        {/* 네 번째 줄: 미끼, 최소 무게, 트로피, 유저명 */}
        <div className="filter-row">
          {/* 미끼 */}
          <div className="filter-group">
            <label className="filter-label">미끼:</label>
            <input
              type="text"
              className="filter-input"
              placeholder="미끼 입력..."
              value={inputFilters.bait}
              onChange={(e) => setInputFilters(prev => ({ ...prev, bait: e.target.value }))}
            />
          </div>

          {/* 최소 무게 */}
          <div className="filter-group">
            <label className="filter-label">최소 무게(kg):</label>
            <input
              type="number"
              className="filter-input"
              placeholder="0.0"
              step="0.001"
              min="0"
              value={inputFilters.minWeight}
              onChange={(e) => setInputFilters(prev => ({ ...prev, minWeight: e.target.value }))}
            />
          </div>

          {/* 트로피 필터 */}
          <div className="filter-group">
            <label className="filter-label">트로피:</label>
            <MultiSelectDropdown
              options={[
                { value: 'trophy', label: '🏆 트로피' },
                { value: 'rare', label: '💎 레어 트로피' },
                { value: 'none', label: '일반' },
              ]}
              selected={filters.trophyFilter}
              onChange={(val) => handleMultiToggle('trophyFilter', val)}
              placeholder="전체"
            />
          </div>

          {/* 유저명 */}
          <div className="filter-group">
            <label className="filter-label">유저명:</label>
            <input
              type="text"
              className="filter-input"
              placeholder="유저명 입력..."
              value={inputFilters.player}
              onChange={(e) => setInputFilters(prev => ({ ...prev, player: e.target.value }))}
            />
          </div>
        </div>

        {/* 다섯 번째 줄: 정렬 기준 */}
        <div className="filter-row">
          {/* 정렬 기준 */}
          <div className="filter-group">
            <label className="filter-label">정렬 기준:</label>
            <select
              className="filter-select"
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="time">시간순</option>
              <option value="weight">크기순</option>
            </select>
          </div>
        </div>

        {/* 검색 및 초기화 버튼 */}
        <div className="filter-buttons">
          <div style={{ display: 'flex', gap: '15px', margin: '0 auto' }}>
            <button 
              className="btn btn-primary"
              onClick={fetchData}
            >
              🔍 검색
            </button>
            <button 
              className="btn btn-secondary"
              onClick={resetFilters}
            >
              🔄 초기화
            </button>
          </div>
          <button 
            className="btn btn-info"
            onClick={() => fetchAllLocationStats()}
          >
            🗺️ 전체 수역 통계
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      {/* 데이터 테이블 */}
      {loading ? (
        <div className="spinner-container">
          <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
        </div>
      ) : (
        <div className="page-content">
          <div style={{ marginBottom: '10px', textAlign: 'right', color: '#666' }}>
            총 {totalCount !== null ? totalCount : '-'}개의 데이터
            {lastUpdated && (
              <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#888' }}>
                (최종 갱신: {formatLastUpdated(lastUpdated)})
              </span>
            )}
          </div>
          
          <div className="table-container">
            <table className="records-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>수역</th>
                  <th>채비</th>
                  <th>어종</th>
                  <th>무게</th>
                  <th>미끼/유저정보</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  data.map((record, idx) => (
                    <RecordRow
                      key={record.id || idx}
                      record={record}
                      formatDate={formatDate}
                      translateCategory={translateCategory}
                      translateRegion={translateRegion}
                      getFlagImage={getFlagImage}
                      formatWeight={formatWeight}
                      processBait={processBait}
                      handleLocationClick={handleLocationClick}
                      handleFishClick={handleFishClick}
                      handleBaitClick={handleBaitClick}
                      baitSystemIds={baitSystemIds}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Comments pageName="records" />

      {/* 통계 모달 */}
      <StatisticsModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        type={modalState.type}
        itemName={modalState.itemName}
        initialFilters={filters} // records 페이지의 현재 필터를 전달
      />

      {/* 전체 수역 통계 모달 */}
      {allLocationsModalOpen && (
        <div className="modal-overlay" onClick={() => setAllLocationsModalOpen(false)}>
          <div className="modal-content all-locations-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗺️ 전체 수역 통계</h2>
              <button className="modal-close" onClick={() => setAllLocationsModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* 날짜 필터 */}
              <div className="modal-date-filters">
                <div className="date-inputs">
                  <div className="filter-item">
                    <label>시작 날짜:</label>
                    <input
                      type="date"
                      value={modalDateFilters.startDate}
                      onChange={(e) => handleModalDateChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="filter-item">
                    <label>종료 날짜:</label>
                    <input
                      type="date"
                      value={modalDateFilters.endDate}
                      onChange={(e) => handleModalDateChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* 날짜 빠른 설정 버튼들 */}
                <div className="date-quick-buttons">
                  <button
                    type="button"
                    className={`date-quick-btn ${modalActiveDateRange === 'today' ? 'active' : ''}`}
                    onClick={() => setModalDateRange('today')}
                  >
                    📅 오늘
                  </button>
                  <button
                    type="button"
                    className={`date-quick-btn ${modalActiveDateRange === 'yesterday' ? 'active' : ''}`}
                    onClick={() => setModalDateRange('yesterday')}
                  >
                    📅 어제
                  </button>
                  <button
                    type="button"
                    className={`date-quick-btn ${modalActiveDateRange === 'week' ? 'active' : ''}`}
                    onClick={() => setModalDateRange('week')}
                  >
                    📅 일주일
                  </button>
                  <button
                    type="button"
                    className={`date-quick-btn ${modalActiveDateRange === 'month' ? 'active' : ''}`}
                    onClick={() => setModalDateRange('month')}
                  >
                    📅 30일
                  </button>
                </div>
              </div>
              
              {locationStatsLoading ? (
                <div className="loading-container" style={{ padding: '60px 20px', textAlign: 'center' }}>
                  <LoadingSpinner height={60} width={60} />
                  <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
                    수역 통계 로딩 중...
                  </p>
                </div>
              ) : (
                <div className="statistics-table-wrapper">
                  {locationStats && locationStats.length > 0 ? (
                    <table className="statistics-table">
                      <thead>
                        <tr>
                          <th>순위</th>
                          <th>수역</th>
                          <th>기록 수</th>
                          <th>미끼 종류</th>
                          <th>어종 수</th>
                          <th>최대 무게</th>
                          <th>상세</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationStats.map((location, index) => (
                          <tr 
                            key={index}
                            className="location-row"
                            onClick={() => {
                              setAllLocationsModalOpen(false);
                              setModalState({
                                isOpen: true,
                                type: 'location',
                                itemName: location.name
                              });
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{index + 1}</td>
                            <td>{location.name}</td>
                            <td>{location.record_count}회</td>
                            <td>{location.bait_count}종</td>
                            <td>{location.fish_count}종</td>
                            <td>{parseFloat(location.max_weight || 0).toFixed(3)} kg</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAllLocationsModalOpen(false);
                                  setModalState({
                                    isOpen: true,
                                    type: 'location',
                                    itemName: location.name
                                  });
                                }}
                              >
                                📊 통계 보기
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-data">
                      <p>수역 통계 데이터가 없습니다.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Records;
